import { useCallback, useEffect, useMemo } from 'react';

import { usePaywallContext } from '~context/paywall';

import { events } from '../names';
import { useSendEvent } from '../useSendEvent';

export const useSendMsgButton = () => useSendEvent(events.chat.sendMsgButton);

export const useSendMsgKeyboard = () =>
	useSendEvent(events.chat.sendMsgKeyboard);

export const useChatClickFromList = () => {
	return useSendEvent(events.chat.chatClickFromList);
};

export const useChatClickTopic = () => {
	return useSendEvent(events.chat.chatClickTopic);
};

export const useChatContactsShowed = () =>
	useSendEvent(events.chat.chatContactsShowed, undefined, {
		forceSendDemoUser: true,
		includeDemoUserMarker: true,
	});

export const useBackToChatList = ({
	unreadCount,
	botName,
	botClientId,
}: {
	unreadCount: number;
	botName: string;
	botClientId: number | string;
}) => {
	const params = useMemo(
		() => ({
			qty_notifications: unreadCount,
			bot_name: botName,
			bot_UserId: botClientId,
		}),
		[botClientId, botName, unreadCount]
	);

	return useSendEvent(events.chat.backToChatList, params);
};

export const useCopyPaste = () => {
	const sendCopy = useSendEvent<{ text?: string }>(events.chat.copyText);
	const sendPaste = useSendEvent<{ text?: string }>(events.chat.pasteText);
	const { isOpenPaymentForm } = usePaywallContext();

	const onCopy = useCallback(() => {
		if (isOpenPaymentForm) return;

		const text = document.getSelection()?.toString();
		void sendCopy({ text });
	}, [sendCopy, isOpenPaymentForm]);

	const onPaste = useCallback(
		(e: ClipboardEvent) => {
			const text = e.clipboardData?.getData('text');
			void sendPaste({ text });
		},
		[sendPaste]
	);

	useEffect(() => {
		addEventListener('copy', onCopy);
		addEventListener('paste', onPaste);

		return () => {
			removeEventListener('copy', onCopy);
			removeEventListener('paste', onPaste);
		};
	}, [onCopy, onPaste]);
};

export const useOpenGiftFeed = ({
	botName,
	botClientId,
}: {
	botName: string;
	botClientId: number;
}) =>
	useSendEvent(events.chat.giftFeedOpened, {
		bot_name: botName,
		bot_UserId: botClientId,
	});

export const useRegenerationClicked = () =>
	useSendEvent(events.chat.regenerationClicked);

export const useResetMemoryClicked = () =>
	useSendEvent(events.chat.resetMemoryClicked, { screen: 'bot-profile' });

export const useResetMemoryConfirmed = () =>
	useSendEvent(events.chat.resetMemoryConfirmed, { screen: 'bot-profile' });

export const useResetMemoryCanceled = () =>
	useSendEvent(events.chat.resetMemoryCanceled, { screen: 'bot-profile' });
